import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  Loader2,
  RefreshCw,
  RotateCcw,
  Save,
  Star,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ScrollArea } from "~/components/ui/scroll-area";
import {
  $getAdminModelCatalog,
  $refreshModelsDevCatalog,
  $saveAdminModelCatalog,
  $seedAdminModelCatalog,
} from "~/lib/admin/model-catalog-mutations";
import type { CatalogProviderId } from "~/lib/ai/models-dev";
import { CATALOG_PROVIDERS } from "~/lib/ai/models-dev";
import { formatModelName } from "~/lib/ai/model-formatter";

export const Route = createFileRoute("/(authenticated)/dashboard/admin/models/")({
  component: AdminModelsPage,
});

interface LocalEntry {
  modelId: string;
  sortOrder: number;
  isCurated: boolean;
  name: string;
  contextWindow: number | null;
  inputCostPerM: number | null;
  status: string | null;
}

function AdminModelsPage() {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<CatalogProviderId>("vercel-gateway");
  const [search, setSearch] = useState("");
  const [defaultModelId, setDefaultModelId] = useState("");
  const [entries, setEntries] = useState<LocalEntry[]>([]);
  const [dirty, setDirty] = useState(false);
  const [showDeprecated, setShowDeprecated] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-model-catalog", provider],
    queryFn: () => $getAdminModelCatalog({ data: { provider } }),
  });

  const refreshCatalogMutation = useMutation({
    mutationFn: async () => {
      await $refreshModelsDevCatalog();
      return $getAdminModelCatalog({ data: { provider, forceRefresh: true } });
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(["admin-model-catalog", provider], fresh);
      toast.success(`Refreshed ${fresh.modelsDev.length} models from models.dev`);
    },
    onError: () => {
      toast.error("Failed to refresh from models.dev");
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const catalogByModelId = new Map(
      data.catalog.entries.map((entry) => [entry.modelId, entry]),
    );

    const merged: LocalEntry[] = data.modelsDev.map((model, index) => {
      const saved = catalogByModelId.get(model.modelId);
      return {
        modelId: model.modelId,
        name: model.name,
        contextWindow: model.contextWindow,
        inputCostPerM: model.inputCostPerM,
        status: model.status,
        sortOrder: saved?.sortOrder ?? index + 1000,
        isCurated: saved?.isCurated ?? false,
      };
    });

    merged.sort((a, b) => {
      if (a.isCurated !== b.isCurated) {
        return a.isCurated ? -1 : 1;
      }
      return a.sortOrder - b.sortOrder;
    });

    const curated = merged.filter((entry) => entry.isCurated);
    curated.forEach((entry, index) => {
      entry.sortOrder = index;
    });

    setEntries(merged);
    setDefaultModelId(data.catalog.defaultModelId);
    setDirty(false);
    setSearch("");
  }, [data, provider]);

  const filteredEntries = useMemo(() => {
    const query = search.trim().toLowerCase();
    return entries.filter((entry) => {
      if (!showDeprecated && entry.status === "deprecated") {
        return false;
      }
      if (!query) {
        return true;
      }
      return (
        entry.modelId.toLowerCase().includes(query) ||
        entry.name.toLowerCase().includes(query)
      );
    });
  }, [entries, search, showDeprecated]);

  const curatedEntries = useMemo(
    () =>
      [...entries]
        .filter((entry) => entry.isCurated)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [entries],
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = entries.map((entry) => ({
        modelId: entry.modelId,
        sortOrder: entry.sortOrder,
        isCurated: entry.isCurated,
      }));

      await $saveAdminModelCatalog({
        data: {
          provider,
          defaultModelId,
          entries: payload,
        },
      });
    },
    onSuccess: () => {
      toast.success("Model catalog saved");
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["admin-model-catalog", provider] });
      queryClient.invalidateQueries({ queryKey: ["provider-models"] });
      queryClient.invalidateQueries({ queryKey: ["provider-default-models"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save catalog");
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => $seedAdminModelCatalog({ data: { provider } }),
    onSuccess: () => {
      toast.success("Imported static defaults");
      queryClient.invalidateQueries({ queryKey: ["admin-model-catalog", provider] });
    },
    onError: () => {
      toast.error("Failed to import defaults");
    },
  });

  function toggleCurated(modelId: string, checked: boolean) {
    setDirty(true);
    setEntries((prev) => {
      const next = prev.map((entry) =>
        entry.modelId === modelId ? { ...entry, isCurated: checked } : entry,
      );
      const curated = next.filter((e) => e.isCurated);
      curated.forEach((entry, index) => {
        entry.sortOrder = index;
      });
      return [...next];
    });
  }

  function moveModel(modelId: string, direction: -1 | 1) {
    setDirty(true);
    setEntries((prev) => {
      const curated = prev
        .filter((e) => e.isCurated)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const index = curated.findIndex((e) => e.modelId === modelId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= curated.length) {
        return prev;
      }
      const swapped = [...curated];
      [swapped[index], swapped[target]] = [swapped[target], swapped[index]];
      swapped.forEach((entry, i) => {
        entry.sortOrder = i;
      });
      const orderMap = new Map(swapped.map((e) => [e.modelId, e.sortOrder]));
      return prev.map((entry) =>
        orderMap.has(entry.modelId)
          ? { ...entry, sortOrder: orderMap.get(entry.modelId)! }
          : entry,
      );
    });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Model catalog</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label htmlFor="provider">Provider</Label>
            <select
              id="provider"
              className="border-input bg-background h-9 min-w-[220px] rounded-md border px-3 text-sm"
              value={provider}
              onChange={(event) => {
                setProvider(event.target.value as CatalogProviderId);
              }}
            >
              {CATALOG_PROVIDERS.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="default-model">Default model</Label>
            <select
              id="default-model"
              className="border-input bg-background h-9 min-w-[280px] rounded-md border px-3 text-sm"
              value={defaultModelId}
              onChange={(event) => {
                setDefaultModelId(event.target.value);
                setDirty(true);
              }}
            >
              {curatedEntries.length === 0 ? (
                <option value={defaultModelId}>{defaultModelId || "—"}</option>
              ) : (
                curatedEntries.map((entry) => (
                  <option key={entry.modelId} value={entry.modelId}>
                    {formatModelName(entry.modelId).name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={refreshCatalogMutation.isPending || isLoading}
              onClick={() => refreshCatalogMutation.mutate()}
            >
              {refreshCatalogMutation.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 size-4" />
              )}
              Refresh from models.dev
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={seedMutation.isPending || isLoading}
              onClick={() => seedMutation.mutate()}
            >
              <RotateCcw className="mr-2 size-4" />
              Import static defaults
            </Button>
            <Button
              type="button"
              disabled={!dirty || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Save className="mr-2 size-4" />
              )}
              Save
            </Button>
          </div>

          {data && !data.catalog.hasDbConfig && (
            <p className="text-muted-foreground w-full text-sm">
              No saved catalog yet — using static defaults in production until you
              save.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search models…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-md"
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={showDeprecated}
            onCheckedChange={(checked) => setShowDeprecated(checked === true)}
          />
          Show deprecated
        </label>
        <Badge variant="secondary">
          {curatedEntries.length} curated / {filteredEntries.length} shown /{" "}
          {entries.length} total
        </Badge>
        {(isLoading || isFetching || refreshCatalogMutation.isPending) && (
          <Loader2 className="size-4 animate-spin" />
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[min(70vh,720px)]">
            <div className="divide-y">
              {isLoading && (
                <p className="text-muted-foreground p-4 text-sm">Loading models…</p>
              )}
              {!isLoading && filteredEntries.length === 0 && (
                <p className="text-muted-foreground p-4 text-sm">No models match.</p>
              )}
              {filteredEntries.map((entry) => {
                const curatedIndex = curatedEntries.findIndex(
                  (e) => e.modelId === entry.modelId,
                );
                const isDefault = entry.modelId === defaultModelId;

                return (
                  <div
                    key={entry.modelId}
                    className="hover:bg-muted/40 flex items-center gap-3 px-4 py-2.5"
                  >
                    <Checkbox
                      checked={entry.isCurated}
                      onCheckedChange={(checked) =>
                        toggleCurated(entry.modelId, checked === true)
                      }
                      aria-label={`Curate ${entry.modelId}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">
                          {formatModelName(entry.modelId).name}
                        </span>
                        {isDefault && (
                          <Badge variant="default" className="gap-1">
                            <Star className="size-3" />
                            default
                          </Badge>
                        )}
                        {entry.status === "deprecated" && (
                          <Badge variant="secondary">deprecated</Badge>
                        )}
                        {entry.isCurated && curatedIndex >= 0 && (
                          <Badge variant="outline">#{curatedIndex + 1}</Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground truncate font-mono text-xs">
                        {entry.modelId}
                      </p>
                    </div>
                    <div className="text-muted-foreground hidden text-right text-xs sm:block">
                      {entry.contextWindow
                        ? `${(entry.contextWindow / 1000).toFixed(0)}k ctx`
                        : "—"}
                      {entry.inputCostPerM != null && (
                        <span className="ml-2">${entry.inputCostPerM}/M in</span>
                      )}
                    </div>
                    {entry.isCurated && (
                      <div className="flex shrink-0 gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          disabled={curatedIndex <= 0}
                          onClick={() => moveModel(entry.modelId, -1)}
                          aria-label="Move up"
                        >
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          disabled={
                            curatedIndex < 0 || curatedIndex >= curatedEntries.length - 1
                          }
                          onClick={() => moveModel(entry.modelId, 1)}
                          aria-label="Move down"
                        >
                          <ArrowDown className="size-4" />
                        </Button>
                        {!isDefault && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setDefaultModelId(entry.modelId);
                              setDirty(true);
                            }}
                          >
                            Set default
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
